import type { ConstructionLine, Project } from "../Project";
import { projectConstructionLines } from "../Project";
import {
  constraintsForLine,
  pruneConstraintsForDeletedLines,
} from "../constraints";
import {
  chainReachableLines,
  isConstraintEnforced,
  applyConstraint,
} from "../constraintEnforcement";
import { settleSnapReshapedLines } from "../constructionLineNormalize";
import { constraintIcon } from "../../icons/constraintIcons";
import type {
  Connection,
  ConnectionChipSpec,
  ConnectionRowSpec,
} from "./Connection";

/** Issue #180 follow-up: the snap (coincident-constraint) façade
 *  that plugs into the unified Connections registry. All real work
 *  still lives in `src/model/constraints.ts` and
 *  `src/model/constraintEnforcement.ts` — this module is just a
 *  thin shim that exposes those helpers through the `Connection`
 *  interface so the layers panel, properties panel, and drag
 *  pipeline can iterate one list instead of three. */
export const snapConnection: Connection = {
  kind: "snap",
  // First in display + commit-pipeline order. Snap has no
  // commit-time `derive` (its enforcement runs per-drag inside
  // `propagateCoincidentAfter…`), but its pruning still needs
  // to run before offset/mirror prune so cycle detection downstream
  // sees a coherent project.
  order: 0,

  chipsForLine(line, project, ctx) {
    const involved = constraintsForLine(project, line.id);
    if (involved.length === 0) return [];
    const broken = ctx.brokenIds ?? new Set<string>();
    const chips: ConnectionChipSpec[] = [];
    for (const c of involved) {
      const icon = constraintIcon(c.kind);
      const isBroken = broken.has(c.id);
      const label = isBroken ? `${icon.label} — not satisfied` : icon.label;
      const title = isBroken
        ? `${label} (${icon.engineering})`
        : `${icon.label} (${icon.engineering})`;
      chips.push({
        kind: "snap",
        connectionKey: c.id,
        role: c.kind,
        iconSvg: icon.svg,
        ariaLabel: label,
        title,
        // Snap chips use only the base class — no kind-modifier.
        className: undefined,
        unsatisfied: isBroken,
        // Pass every ref through so the canvas overlay highlights
        // ALL features in the constraint (an anchor pair lights
        // up both rings + the curves on either side).
        hoverRefs: c.refs,
        // Click selects the OTHER line in the constraint (#221) —
        // the first ref not on this chip's own row. Self-referential
        // constraints get no click action.
        clickTarget: (() => {
          const partner = c.refs.find(
            (r) =>
              "constructionId" in r &&
              r.constructionId &&
              r.constructionId !== line.id,
          );
          return partner && "constructionId" in partner
            ? { constructionId: partner.constructionId }
            : undefined;
        })(),
        extraDatasetEntries: [
          ["constraintKind", c.kind],
          ["constraintId", c.id],
          // Legacy dataset key preserved so existing specs that
          // query `[data-constraint-id]` keep working through the
          // refactor.
        ],
      });
    }
    return chips;
  },

  rowsForLine(line, project, ctx) {
    const involved = constraintsForLine(project, line.id);
    if (involved.length === 0) return [];
    const broken = ctx.brokenIds ?? new Set<string>();
    const rows: ConnectionRowSpec[] = [];
    for (const c of involved) {
      const icon = constraintIcon(c.kind);
      const isBroken = broken.has(c.id);
      const label = isBroken ? `${icon.label} — not satisfied` : icon.label;
      const constraintId = c.id;
      rows.push({
        kind: "snap",
        connectionKey: c.id,
        role: c.kind,
        iconSvg: icon.svg,
        ariaLabel: label,
        title: label,
        className: undefined,
        unsatisfied: isBroken,
        hoverRefs: c.refs,
        clickTarget: (() => {
          const partner = c.refs.find(
            (r) =>
              "constructionId" in r &&
              r.constructionId &&
              r.constructionId !== line.id,
          );
          return partner && "constructionId" in partner
            ? { constructionId: partner.constructionId }
            : undefined;
        })(),
        extraDatasetEntries: [
          ["constraintKind", c.kind],
          ["constraintId", c.id],
        ],
        label,
        onDetach: (editor) => {
          editor.commit("Delete constraint", (proj) => ({
            ...proj,
            constraints: (proj.constraints ?? []).filter(
              (existing) => existing.id !== constraintId,
            ),
          }));
        },
        onApply: isConstraintEnforced(c)
          ? (editor) => {
              const scope = editor.scene.getPaperScope();
              editor.commit("Apply constraint", (proj) => {
                const projLines = proj.constructionLines ?? [];
                const enforced = applyConstraint(proj, c, projLines, scope);
                return settleSnapReshapedLines(
                  proj,
                  projLines,
                  enforced,
                  scope,
                );
              });
            }
          : undefined,
      });
    }
    return rows;
  },

  // Snap has no commit-time derive — enforcement runs per-drag in
  // `propagateCoincidentAfter…`. The deriveAllConnections pipeline
  // calls this hook anyway, so omit `derive` to short-circuit.

  pruneForDeletedLines({ project, deletedLineIds }) {
    if (deletedLineIds.size === 0) return project;
    const next = pruneConstraintsForDeletedLines(
      project.constraints,
      deletedLineIds,
    );
    if (next === project.constraints) return project;
    return { ...project, constraints: next };
  },

  // No per-frame rederive — chain translation runs through
  // `translateDragClosure` inside the drag handler, which already
  // knows how to walk the snap graph.

  forwardClosure({ project, originIds }) {
    // `chainReachableLines` returns origins ∪ reachable; strip
    // origins for parity with the historic `reachableOffsets`
    // contract (callers union-then-subtract).
    const reachable = chainReachableLines(project, originIds);
    for (const id of originIds) reachable.delete(id);
    return reachable;
  },

  resolveRoot(_project, lineId) {
    // Snap has no "root" concept — every participant is equal.
    return lineId;
  },

  isDerived(_line: ConstructionLine) {
    return false;
  },
};

/** Count how many coincident-constraint chips a single line owns —
 *  used by the layers-panel chip-count threshold so the inline-mode
 *  computation can sum chips across all three connection kinds.
 *  Kept exported here so callers don't need to import
 *  `constraintsForLine` directly. */
export function snapChipCountForLine(
  project: Project,
  line: ConstructionLine,
): number {
  // Re-walk projectConstructionLines for symmetry with the other
  // counters — the cost is one O(n) lookup which the layers panel
  // already pays for every render.
  void projectConstructionLines;
  return constraintsForLine(project, line.id).length;
}
